import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useParams, type RouteObject } from 'react-router';
import { RouteError, NotFoundPage } from './components/error-boundary';
import { ModuleRoute, RequireAnonymous, RequireAuth, RequireStaff, RequireTenant } from './components/guards';
import { Shell } from './components/shell';
import type { ApiClient } from './lib/api';
import { createQueryClient } from './lib/query';
import { RealtimeConnection } from './lib/realtime';
import { SessionProvider } from './lib/session';
import { AcceptInvitePage } from './pages/auth/invite';
import { LoginPage } from './pages/auth/login';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/auth/password';
import { SignupPage } from './pages/auth/signup';
import { CategoriesPage } from './pages/catalog/categories';
import { ImportProductsPage, ImportReportPage } from './pages/catalog/import';
import { EditProductPage, NewProductPage } from './pages/catalog/product-edit';
import { ProductsListPage } from './pages/catalog/products-list';
import { storefrontRoutes } from './pages/storefront/routes';
import { checkoutRoutes } from './pages/checkout/routes';
import { ordersRoutes } from './pages/orders/routes';
import { subscriptionsRoutes } from './pages/subscriptions/routes';
import { agendaRoutes } from './pages/agenda/routes';
import { inventoryRoutes } from './pages/inventory/routes';
import { financeRoutes } from './pages/finance/routes';
import { fiscalRoutes } from './pages/fiscal/routes';
import { inboxRoutes } from './pages/inbox/routes';
import { adsRoutes } from './pages/ads/routes';
import { AdsReviewDetailPage, AdsReviewQueuePage, AlertsPage, BackofficeIndex, BackofficeLayout, PlatformSettingsPage, QueuesPage, TenantDetailPage, TenantsSearchPage, WebhooksPage } from './pages/backoffice/backoffice';
import { BillingPage } from './pages/billing/billing';
import { UsagePage } from './pages/billing/usage';
import { DashboardPage } from './pages/dashboard/dashboard';
import { HelpPage } from './pages/help/help';
import { NotificationsPage } from './pages/notifications/notifications';
import { CustomerDetailPage, CustomersListPage } from './pages/customers/customers';
import { CreateTenantPage } from './pages/onboarding/create-tenant';
import { QuickSalePage } from './pages/sales/quick-sale';
import { BusinessSettingsPage, NotificationSettingsPage, SecuritySettingsPage, SettingsIndex, SettingsLayout, SupportAccessPage } from './pages/settings/settings';
import { TeamPage } from './pages/team/team';

/** Links antigos da API (`/catalogo/produtos/...`) apontam para a mesma tela. */
function CatalogRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/produtos/${id}` : '/produtos'} replace />;
}

export const routes: RouteObject[] = [
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [
          { path: '/entrar', element: <LoginPage /> },
          { path: '/criar-conta', element: <SignupPage /> },
          { path: '/esqueci-a-senha', element: <ForgotPasswordPage /> },
        ],
      },
      { path: '/redefinir-senha', element: <ResetPasswordPage /> },
      { path: '/verificar-email', element: <VerifyEmailPage /> },
      { path: '/convite', element: <AcceptInvitePage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/novo-negocio', element: <CreateTenantPage /> },
          {
            path: '/interno',
            element: <RequireStaff />,
            children: [
              {
                element: <Shell />,
                errorElement: <RouteError />,
                children: [
                  {
                    element: <BackofficeLayout />,
                    children: [
                      { index: true, element: <BackofficeIndex /> },
                      { path: 'negocios', element: <TenantsSearchPage /> },
                      { path: 'negocios/:id', element: <TenantDetailPage /> },
                      { path: 'alertas', element: <AlertsPage /> },
                      { path: 'anuncios', element: <AdsReviewQueuePage /> },
                      { path: 'anuncios/:tenantId/:id', element: <AdsReviewDetailPage /> },
                      { path: 'filas', element: <QueuesPage /> },
                      { path: 'webhooks', element: <WebhooksPage /> },
                      { path: 'parametros', element: <PlatformSettingsPage /> },
                    ],
                  },
                ],
              },
            ],
          },
          {
            element: <RequireTenant />,
            children: [
              {
                element: <Shell />,
                errorElement: <RouteError />,
                children: [
                  { index: true, element: <ModuleRoute permission="dashboard:read"><DashboardPage /></ModuleRoute> },
                  {
                    path: 'produtos',
                    element: <ModuleRoute permission="catalog:read" />,
                    children: [
                      { index: true, element: <ProductsListPage /> },
                      { path: 'novo', element: <NewProductPage /> },
                      { path: 'importar', element: <ImportProductsPage /> },
                      { path: 'importacoes/:id', element: <ImportReportPage /> },
                      { path: 'categorias', element: <CategoriesPage /> },
                      { path: ':id', element: <EditProductPage /> },
                    ],
                  },
                  {
                    path: 'clientes',
                    element: <ModuleRoute permission="customers:read" />,
                    children: [
                      { index: true, element: <CustomersListPage /> },
                      { path: ':id', element: <CustomerDetailPage /> },
                    ],
                  },
                  { path: 'equipe', element: <ModuleRoute permission="team:read"><TeamPage /></ModuleRoute> },
                  { path: 'catalogo/produtos', element: <CatalogRedirect /> },
                  { path: 'catalogo/produtos/:id', element: <CatalogRedirect /> },
                  { path: 'venda-rapida', element: <ModuleRoute permission="sales:quick"><QuickSalePage /></ModuleRoute> },
                  { path: 'notificacoes', element: <ModuleRoute permission="notifications:read"><NotificationsPage /></ModuleRoute> },
                  {
                    path: 'configuracoes',
                    element: <ModuleRoute permission="tenant:read"><SettingsLayout /></ModuleRoute>,
                    children: [
                      { index: true, element: <SettingsIndex /> },
                      { path: 'negocio', element: <BusinessSettingsPage /> },
                      { path: 'notificacoes', element: <NotificationSettingsPage /> },
                      { path: 'seguranca', element: <SecuritySettingsPage /> },
                      { path: 'suporte', element: <SupportAccessPage /> },
                    ],
                  },
                  {
                    path: 'assinatura',
                    element: <ModuleRoute permission="billing:read" />,
                    children: [
                      { index: true, element: <BillingPage /> },
                      { path: 'consumo', element: <UsagePage /> },
                    ],
                  },
                  { path: 'ajuda', element: <HelpPage /> },
                  ...storefrontRoutes,
                  ...checkoutRoutes,
                  ...ordersRoutes,
                  ...subscriptionsRoutes,
                  ...agendaRoutes,
                  ...inventoryRoutes,
                  ...financeRoutes,
                  ...fiscalRoutes,
                  ...inboxRoutes,
                  ...adsRoutes,
                  { path: '*', element: <NotFoundPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export function App({ client }: { client?: ApiClient }) {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(() => createBrowserRouter(routes));
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider client={client}>
        <RealtimeConnection />
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  );
}
